import { NgModule } from '@angular/core';
import {
  APOLLO_NAMED_OPTIONS,
  APOLLO_OPTIONS,
  ApolloModule,
  NamedOptions,
} from 'apollo-angular';
import { InMemoryCache, ApolloLink, split } from '@apollo/client/core';
import { WebSocketLink } from '@apollo/client/link/ws';
import { HttpLink } from 'apollo-angular/http';
import { HttpHeaders } from '@angular/common/http';
import { environment } from '../environments/environment';
import { setContext } from '@apollo/client/link/context';
import { getMainDefinition } from '@apollo/client/utilities';
import { KeycloakService } from 'keycloak-angular';
import { onError } from '@apollo/client/link/error';
import { LoadingService } from './services/loading/loading.service';
import { BehaviorSubject } from 'rxjs';
import { RetryLink } from 'apollo-link-retry';
import { debounceTime, filter } from 'rxjs/operators';
import { ErrorPolicy } from 'apollo-client';

const secure_key = environment.subscriptionSecureKey;
const networkStatus = new BehaviorSubject(false);
let message = null;
// Use this url for test in local
// const ws_uri = "ws://localhost:8090/graphql/subscription";

// TODO REMOVE: temporary filter to ignore CycleType enum empty value error on prospectus save
const CYCLE_TYPE_EMPTY_ERROR =
  "Enum 'CycleType' cannot represent value: ''";
const CYCLE_TYPE_ERROR_PATH = 'updateProspectus.farmCategory.production.cycle';

function isCycleTypeEmptyError(err: { message?: string; path?: readonly (string | number)[] }): boolean {
  const pathStr = err.path?.join?.('.');
  return !!(
    err.message?.includes(CYCLE_TYPE_EMPTY_ERROR) &&
    pathStr === CYCLE_TYPE_ERROR_PATH
  );
}

function filterCycleTypeErrorLink(): ApolloLink {
  return new ApolloLink((operation, forward) =>
    forward(operation).map((response) => {
      if (!response.errors?.length) return response;
      const filtered = response.errors.filter((e) => !isCycleTypeEmptyError(e));
      if (filtered.length === 0) {
        const { errors, ...rest } = response;
        return rest;
      }
      return { ...response, errors: filtered };
    })
  );
}

export function createApollo(uri: string, httpLink: HttpLink, options?: { filterCycleTypeError?: boolean }) {
  const ws_uri =
    (environment.protocol === 'https' ? 'wss' : 'ws') +
    uri.slice(uri.indexOf(':'));
  const keycloakService = new KeycloakService();
  const basic = setContext((operation, context) => ({
    headers: {
      Accept: 'charset=utf-8',
    },
  }));
  const auth = setContext(async (operation, context) => {
    const token = localStorage.getItem('access_token');
    if (token === null) {
      return {};
    } else {
      return {
        headers: new HttpHeaders({
          access_token: token,
        }),
      };
    }
  });

  const retryLink = new RetryLink();

  const http = ApolloLink.from([basic, auth, httpLink.create({ uri })]);

  const ws = new WebSocketLink({
    uri: ws_uri,
    options: {
      lazy: true,
      reconnect: true,
      connectionParams: async () => {
        const token = localStorage.getItem('access_token');
        return { access_token: token };
      },
    },
  });
  const link = split(
    ({ query }) => {
      let definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    ws,
    http,
  );
  const cache = new InMemoryCache({ addTypename: false });
  const defaultOptions = {
    watchQuery: {
      errorPolicy: 'all' as ErrorPolicy,
    },
  };

  const errorLink = onError(
    ({ graphQLErrors, networkError, operation, forward }) => {
      if (graphQLErrors) {
        const oldHeaders = operation.getContext()['headers'];
        operation.setContext({
          headers: {
            ...oldHeaders,
            authorization: localStorage.getItem('access_token'),
          },
        });
        // retry the request, returning the new observable
        return forward(operation);

        //Use this code in future, in case of different graphQLErrors
        //with the switch-case check the error, based on the error menages it

        // for (let err of graphQLErrors) {
        //   switch (err.extensions.code) {
        //     case 'UNAUTHENTICATED':
        //       // error code is set to UNAUTHENTICATED
        //       // when AuthenticationError thrown in resolver

        //       // modify the operation context with a new token
        //       const oldHeaders = operation.getContext().headers;
        //       operation.setContext({
        //         headers: {
        //           ...oldHeaders,
        //           authorization: keycloakService.getToken(),
        //         },
        //       });
        //       // retry the request, returning the new observable
        //       return forward(operation);
        //   }
        // }
      }
      if (networkError) {
        console.log(`[Network error]:`, networkError);
        networkStatus.next(true);
        message = networkError;
        //return forward(operation);

        // if you would also like to retry automatically on
        // network errors, we recommend that you use
        // apollo-link-retry
      }
    },
  );

  const links: ApolloLink[] = [errorLink, link];
  if (options?.filterCycleTypeError) {
    links.unshift(filterCycleTypeErrorLink());
  }
  const httpLinkWithErrorHandling = ApolloLink.from(links);

  return {
    link: httpLinkWithErrorHandling,
    cache,
    defaultOptions,
    retryLink,
  };
}

@NgModule({
  imports: [ApolloModule],
  providers: [
    {
      provide: KeycloakService,
    },
    {
      provide: APOLLO_NAMED_OPTIONS,
      useFactory(httpLink: HttpLink): NamedOptions {
        return {
          default: createApollo(environment.graphql, httpLink),
          // TODO REMOVE: filterCycleTypeError - remove when backend fixes CycleType empty value
          prospectus: createApollo(environment.prospectusServer, httpLink, { filterCycleTypeError: true }),
          agromodels: createApollo(environment.agromodelsServer, httpLink),
          co2: createApollo(environment.co2, httpLink),
        };
      },
      deps: [HttpLink],
    },
  ],
})
export class GraphQLModule {
  constructor(loadingService: LoadingService) {
    networkStatus
      .pipe(
        filter((v) => v),
        debounceTime(1000),
      )
      .subscribe(() =>
        loadingService.addFatalError({ type: 'NETWORK', message }),
      );
  }
}
